// U4 — intent-driven alternative selection for the pathway comparison framework.
//
// Pure mapping from a project's ProjectIntent (plus optional power-availability
// preferences) onto a recommended posture: which technology presets to feature
// in the picker, which to pre-select for a fresh comparison, and a per-technology
// rationale string explaining WHY each was surfaced.
//
// This makes "Compare alternatives" intent-aware instead of showing the same
// four hardcoded presets for every project. The function is deterministic and
// side-effect free so it can be unit-tested and called during render.

import type { Pathway, PathwayClassification, PhaseSourceMixComponent, TechnologyId } from './types';
import type {
  ProjectIntent,
  ProjectPhase,
  PhasePowerComponentKey,
  PowerAvailabilityPreferences,
} from '../../types/siteRag';
import { PHASE_POWER_COMPONENT_LABEL } from '../../types/siteRag';
import {
  effectivePhases,
  effectivePhaseTimeline,
  formatInServiceDate,
  perSourceIncrementMW,
} from '../projectIntent';
import { phaseFacilityLoadMW } from '../powerStack';
import {
  assessPhaseFeasibility,
  resolveGenerationNameplateForTech,
  firmCapacitySiteContextFromIntent,
  baseloadEnergyCfPct,
} from '../phaseFeasibility';
import { PRESET_BY_TECHNOLOGY, GRID_TARIFF_BENCHMARKS } from './presets';
import { blendPhaseEconomics } from './presets/shared';
import { buildFuelCellEconomicSnapshot } from './presets/stationaryFuelCell';
import {
  resolvePhaseComponentTechnology,
  phaseComponentToTechnologyId,
  technologyIdToPhaseComponent,
  isFuelCellTechnologyId,
} from './resolvePhaseTechnology';
import { KW_PER_MW } from '../projectFinance';
import {
  applyCapacityOverride,
  applyCodYearOverride,
  pathwayDisplayNameWithCod,
} from './pathwayQuickEdit';
import type { SiteContext } from '../../types/siteContext';

export interface RecommendedPathways {
  /**
   * Technologies to surface as primary preset buttons in the picker
   * (the "featured" row). Always non-empty; ordered most-relevant-first.
   * `grid-tariff` is appended as a baseline reference when not already present.
   */
  featured: TechnologyId[];
  /**
   * The 2 alternatives to pre-select when seeding a fresh, unsaved
   * comparison. Always exactly two distinct technologies so the comparison
   * surfaces (MIN_PATHWAYS = 2) render immediately.
   */
  defaultSelection: TechnologyId[];
  /**
   * Per-technology one-line rationale, keyed by TechnologyId. Rendered as a
   * caption under each featured preset and in the "intent defaults" banner.
   */
  rationale: Record<string, string>;
}

/**
 * Compute the intent-driven pathway posture.
 *
 * Posture mapping (first matching rule wins for the PRIMARY driver, then
 * grid-tariff is layered in as a baseline):
 *   - nuclearInterest 'primary'         → feature + preselect nuclear-smr
 *   - btmStrategy 'grid-only'           → grid-tariff + one BTM bridge (mixed)
 *   - priority 'sustainability' / carbon commitment → solar-bess / wind-bess + nuclear
 *   - priority 'speed' OR short timeline (under-2yr) → gas-cc + grid-tariff
 *   - priority 'cost'                   → gas-cc + grid-tariff
 *   - priority 'resilience'             → nuclear-smr + gas-cc
 *   - hybrid / undecided                → mixed + grid-tariff
 *
 * @param intent  The project intent (workload, priority, nuclear, carbon, timeline).
 * @param powerPrefs  Optional power-availability preferences — `btmStrategy`
 *                    lives here (not on ProjectIntent), so callers that have it
 *                    pass it in to refine the grid-vs-BTM posture.
 */
export function recommendedPathwaysForIntent(
  intent: ProjectIntent | null | undefined,
  powerPrefs?: PowerAvailabilityPreferences | null,
): RecommendedPathways {
  const nuclearInterest = intent?.nuclearInterest;
  const priority = intent?.priority;
  const timeline = intent?.timeline;
  const carbonCommitment = intent?.carbonCommitment;
  const btmStrategy = powerPrefs?.btmStrategy;

  const carbonBinding =
    carbonCommitment != null &&
    carbonCommitment !== 'none' &&
    carbonCommitment !== 'custom';
  const shortTimeline = timeline === 'under-2yr';

  // Per-technology rationale — only the ones we surface get an entry, but we
  // keep the strings stable so the picker caption + banner read consistently.
  const RATIONALE: Record<TechnologyId, string> = {
    'nuclear-smr': 'Matches your nuclear interest — clean, firm, dispatchable baseload.',
    'nuclear-large': 'Utility-scale nuclear for firm carbon-free baseload at large capacity.',
    'gas-cc': 'Fastest firm path to power with mature, low-risk technology.',
    'gas-sc': 'Simple-cycle gas for peaking / fast-track bridge capacity.',
    'fuel-cell-sofc': 'Modular stationary SOFC for high-efficiency, low-NOx on-site baseload power.',
    'fuel-cell-pafc': 'Stationary PAFC for firm combined heat and power (CHP) baseload.',
    'fuel-cell-pem': 'Zero-carbon hydrogen PEM fuel-cell power for clean firm capacity.',
    'fuel-cell-mcfc': 'Stationary MCFC for high-temperature industrial/district CHP baseload.',
    'solar-bess': 'Carbon-free generation aligned with your sustainability commitment.',
    'wind-bess': 'Carbon-free generation aligned with your sustainability commitment.',
    'grid-tariff': 'Grid-supplied baseline tariff to benchmark behind-the-meter options against.',
    mixed: 'Hybrid bridge — phase from a fast bridge into your long-term firm source.',
  };

  let featured: TechnologyId[];
  let defaultSelection: TechnologyId[];

  if (nuclearInterest === 'primary') {
    // Nuclear is the headline ask — feature + preselect it, benchmarked vs grid.
    featured = ['nuclear-smr', 'grid-tariff', 'gas-cc', 'gas-sc'];
    defaultSelection = ['nuclear-smr', 'grid-tariff'];
  } else if (btmStrategy === 'grid-only') {
    // Grid-only strategy — lead with the tariff, offer one BTM alternative to
    // compare. Q7: the hybrid bridge used to be the second default here; it was
    // retired because the engine cannot represent a sequential bridge, so the
    // comparison now offers gas-cc, which it can.
    featured = ['grid-tariff', 'gas-cc', 'solar-bess', 'gas-sc'];
    defaultSelection = ['grid-tariff', 'gas-cc'];
  } else if (priority === 'sustainability' || carbonBinding) {
    // Carbon / sustainability driver — renewables first, nuclear as firm clean.
    featured = ['solar-bess', 'wind-bess', 'nuclear-smr', 'grid-tariff'];
    defaultSelection = ['solar-bess', 'nuclear-smr'];
  } else if (priority === 'speed' || shortTimeline) {
    // Time-to-power is the constraint — gas-cc bridge benchmarked vs grid.
    featured = ['gas-cc', 'grid-tariff', 'gas-sc', 'solar-bess'];
    defaultSelection = ['gas-cc', 'grid-tariff'];
  } else if (priority === 'cost') {
    // Lowest landed cost — gas-cc vs grid tariff.
    featured = ['gas-cc', 'grid-tariff', 'solar-bess', 'gas-sc'];
    defaultSelection = ['gas-cc', 'grid-tariff'];
  } else if (priority === 'resilience') {
    // Resilience / firmness — nuclear + firm gas, decoupled from the grid.
    featured = ['nuclear-smr', 'gas-cc', 'grid-tariff', 'solar-bess'];
    defaultSelection = ['nuclear-smr', 'gas-cc'];
  } else {
    // hybrid / undecided — show a balanced spread. Q7: this was anchored on the
    // hybrid-bridge preset, so the DEFAULT recommendation for an undecided
    // project was a pathway whose economics the engine fabricated. Anchored on
    // gas-cc (the fastest firm BTM option) benchmarked against the grid.
    featured = ['gas-cc', 'grid-tariff', 'nuclear-smr', 'solar-bess'];
    defaultSelection = ['gas-cc', 'grid-tariff'];
  }

  // Always keep grid-tariff available as the baseline reference.
  if (!featured.includes('grid-tariff')) {
    featured = [...featured, 'grid-tariff'];
  }

  // Build the rationale map for exactly the surfaced technologies (union of
  // featured + defaultSelection) so the caller never indexes a missing key.
  const surfaced = new Set<TechnologyId>([...featured, ...defaultSelection]);
  const rationale: Record<string, string> = {};
  for (const tech of surfaced) {
    rationale[tech] = RATIONALE[tech];
  }

  return { featured, defaultSelection, rationale };
}

// ─────────────────────────────────────────────────────────────────────────────
// Stage 3 — intent-FAITHFUL pathways straight from the phased rollout.
//
// `recommendedPathwaysForIntent` above is a posture HEURISTIC: it surfaces a
// curated picker selection from coarse intent signals (priority / nuclear /
// carbon) and deliberately IGNORES `intent.phases`. That is wrong for a
// phased project — the user has already told us, per phase, the sources,
// nameplate MW, and COD. The functions below walk `effectivePhases(intent)`
// and emit pathways that mirror that actual rollout (one candidate per
// alternative source in OR phases; one blended `sourceMix` pathway per AND
// phase), at each phase's real capacity + COD year.
//
// Economics are NEVER re-derived here: single-tech pathways come straight
// from the `PRESET_BY_TECHNOLOGY` factories (NREL ATB / EIA AEO / Lazard);
// blended AND pathways carry a `sourceMix` so the existing cashflow engine
// (which calls `blendPhaseEconomics`) drives capex / MWh. We only resize
// (`applyCapacityOverride`) and re-anchor the COD (`applyCodYearOverride`).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Bridge a granular per-phase generation/storage component onto the financial
 * `TechnologyId` whose sourced economics represent it. `battery` returns
 * `null` — a BESS is storage, not a point-source generator, so it has no
 * standalone single-tech pathway (it participates only as part of an AND
 * blend's storage context). Mirrors `SINGLE_COMPONENT_POWER_SOURCE` in
 * `projectIntent.ts` but targets the finance preset ids (e.g. grid →
 * grid-tariff, not the coarse `grid` PowerSourceKey).
 */
export { phaseComponentToTechnologyId };

/** Short, label-friendly tech name for a pathway title (e.g. "Grid", "SMR"). */
const TECHNOLOGY_SHORT_LABEL: Record<TechnologyId, string> = {
  'gas-cc': 'Gas CC',
  'gas-sc': 'Gas SC',
  'fuel-cell-sofc': 'Fuel Cell (SOFC)',
  'fuel-cell-pafc': 'Fuel Cell (PAFC)',
  'fuel-cell-pem': 'Fuel Cell (PEM H2)',
  'fuel-cell-mcfc': 'Fuel Cell (MCFC)',
  'nuclear-smr': 'SMR',
  'nuclear-large': 'Large Nuclear',
  'solar-bess': 'Solar+BESS',
  'wind-bess': 'Wind+BESS',
  'grid-tariff': 'Grid',
  mixed: 'Blend',
};

/**
 * Per-COMPONENT leg label — the reader-facing name of a power-mix leg that
 * surfaces the TRUE source instead of the coarse `TechnologyId`. Critically a
 * `recip` component reads "Reciprocating Engine", NOT "Gas SC" — both bridge to
 * the `gas-sc` finance preset (whose economics ARE recip economics, see
 * gasSimpleCycle.ts), but the granular component carries the distinct label.
 *
 * This is the source of truth for naming legs in pathway titles. It mirrors the
 * editor chips in `PHASE_POWER_COMPONENT_LABEL` but reads as a standalone leg
 * name (e.g. "Reciprocating Engine" rather than the terse "Recip engines").
 */
const COMPONENT_LEG_LABEL: Record<PhasePowerComponentKey, string> = {
  recip: 'Reciprocating Engine',
  'gas-turbine': 'Gas CC',
  'fuel-cell': 'Stationary Fuel Cell',
  battery: 'Battery (BESS)',
  grid: 'Grid import',
  'solar-bess': 'Solar+BESS',
  'wind-bess': 'Wind+BESS',
  smr: 'SMR',
  'large-nuclear': 'Large Nuclear',
};

/**
 * Label a single pathway leg. Prefers the granular component (so `recip` reads
 * "Reciprocating Engine"); falls back to the coarse `TechnologyId` short label
 * when no originating component is known (legacy / posture-seeded pathways).
 */
function legLabel(tech: TechnologyId, component?: PhasePowerComponentKey): string {
  if (component === 'fuel-cell' && isFuelCellTechnologyId(tech)) {
    return TECHNOLOGY_SHORT_LABEL[tech];
  }
  if (component) return COMPONENT_LEG_LABEL[component] ?? PHASE_POWER_COMPONENT_LABEL[component];
  return TECHNOLOGY_SHORT_LABEL[tech];
}

/**
 * One generation leg of an AND-mode phase — the finance preset id PLUS the
 * granular component it originated from (so recip stays distinguishable from a
 * direct gas-sc source) and its capacity within the phase (MW).
 */
interface PhaseGenerationLeg {
  tech: TechnologyId;
  component: PhasePowerComponentKey;
  fractionMw: number;
}

/**
 * Build an explicit `unsupported-incomplete` pathway when a phase component
 * cannot be resolved to a valid technology or chemistry (e.g. `fuel-cell` with
 * `chemistry: 'unspecified'`, or `chemistry: 'compare-all-suitable'` in an AND
 * blend). Carries `phases: []` so `computePathwayLcoe` returns `0` energy and
 * `reportLcoeSurface` gates it out of rankings.
 */
function buildUnsupportedIncompletePhasePathway(
  phase: ProjectPhase,
  phaseIndex: number,
  intent: ProjectIntent,
  holdId: string,
  holdReason: string,
): Pathway {
  const phaseLoadMw = phaseFacilityLoadMW(phase, intent) ?? phase.capacityMW ?? 100;
  const phaseLabel = phase.label || `Phase ${phaseIndex + 1}`;
  return {
    id: `unsupported_fuel_cell_phase_${phaseIndex}`,
    name: `${phaseLabel} Stationary Fuel Cell (Unspecified / Hold)`,
    classification: 'btm',
    technology: 'fuel-cell-sofc',
    capacityMW: phaseLoadMw,
    facilityLoadMW: phaseLoadMw,
    phases: [],
    capex: {
      baseUsd: 0,
      contingencyPct: 0,
      outflowsByYear: [],
    },
    opex: {
      fixedOmPerKwYearUsd: 0,
      variableOmPerMwhUsd: 0,
      fuelCostPerMwhUsd: 0,
    },
    revenue: {
      ppaPricePerMwhUsd: 65,
      ppaEscalatorPctPerYear: 2,
      ppaTermYears: 20,
      availabilityPct: 0,
    },
    risk: {
      technologyMaturity: 'commercial-but-evolving',
      regulatoryRisk: 'medium',
      fuelPriceRisk: 'medium',
      baseWaccPct: 7.5,
      riskPremiumBps: 150,
    },
    carbon: {
      tCo2ePerMwh: 0,
    },
    lifetime: {
      designLifeYears: 20,
      decommissioningUsd: 0,
      decommissioningYear: 2050,
    },
    economicQualificationPosture: 'unsupported-incomplete',
    relianceHolds: [
      {
        id: holdId,
        domain: 'unresolved-chemistry-or-fuel',
        severity: 'blocking-hold',
        summary: holdReason,
        remediationNextStep:
          'Select an explicit stationary fuel-cell chemistry (SOFC, PAFC, PEMFC, or MCFC) and primary fuel.',
      },
    ],
  };
}

/**
 * Classify a multi-source AND-mode mix. A single distinct generation tech is
 * NOT a hybrid — it falls through to a single-tech pathway upstream (so it gets
 * a distinct, technology-specific name + classification). A mix that spans the
 * grid PLUS on-site generation, or multiple distinct on-site techs, is a true
 * 'hybrid'. (The single-tech case never reaches this helper.)
 */
function classifyMix(legs: ReadonlyArray<PhaseGenerationLeg>): PathwayClassification {
  const techs = new Set(legs.map((l) => l.tech));
  if (techs.size <= 1) {
    // Single distinct tech — grid alone → 'grid', any single BTM tech → 'btm'.
    const only = legs[0]?.tech;
    return only === 'grid-tariff' ? 'grid' : 'btm';
  }
  return 'hybrid';
}

/** Resolve the COD calendar year for a phase, defaulting to the preset's. */
function phaseCodYear(phase: ProjectPhase, now: Date): number | null {
  const iso = effectivePhaseTimeline(phase, now).inServiceDate;
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  if (!Number.isFinite(d.getTime())) return null;
  return d.getUTCFullYear();
}

/**
 * Resolve the COD *display token* for a phase label.
 *
 * The phase carries a MONTH-resolution COD when the user typed an exact
 * `targetInServiceDate` (ISO `YYYY-MM-DD`): in that case return a
 * month-precision `"MMM YYYY"` string (e.g. `"Jun 2029"`) so two milestones in
 * the SAME calendar year (e.g. Jun 2029 vs Dec 2029) stay distinguishable.
 * When the phase only carries a coarse year/bucket (no exact date) return the
 * 4-digit year as a string. Returns `null` when the phase has no resolvable COD
 * at all (the caller falls back to the pathway-derived year). The COD year used
 * to re-anchor the pathway is unchanged — only the LABEL gains month precision.
 */
function phaseCodLabel(phase: ProjectPhase, now: Date): string | null {
  const timeline = effectivePhaseTimeline(phase, now);
  if (timeline.isExact && timeline.inServiceDate) {
    // formatInServiceDate → "Jun 2029" (month-short + numeric year, UTC-stable).
    return formatInServiceDate(timeline.inServiceDate);
  }
  const year = phaseCodYear(phase, now);
  return year != null ? String(year) : null;
}

/**
 * Build a single-tech pathway sized + anchored to a phase. Starts from the
 * technology preset (sourced economics), then resizes to the phase capacity
 * and re-anchors the COD to the phase target year. When the phase carries no
 * capacity / COD the corresponding preset default is kept unchanged.
 */
function singleTechPathwayForPhase(
  tech: TechnologyId,
  phase: ProjectPhase,
  phaseIndex: number,
  intent: ProjectIntent,
  siteContext: SiteContext,
  now: Date,
  /**
   * Originating granular component, when known. Drives recip-aware leg naming:
   * a `recip`-origin single-tech leg reads "Reciprocating Engine", not the
   * coarse "Gas SC" that `tech === 'gas-sc'` would otherwise yield.
   */
  component?: PhasePowerComponentKey,
): Pathway {
  // Q7 — `mixed` no longer has a preset factory (the hybrid bridge was retired
  // as unrepresentable). A phase asking for it names a CONCURRENT blend, which
  // is built by `buildBlendedPathway` below, not by a single-tech factory.
  const factory = PRESET_BY_TECHNOLOGY[tech];
  if (!factory) {
    throw new RangeError(
      `buildPhasePathway: '${tech}' has no single-technology preset. ` +
        'A blended phase must be built from its sourceMix, not from a factory.',
    );
  }
  let pathway = factory(intent, siteContext, {
    fuelCellConfig: phase.powerSourceConfig?.fuelCellConfig,
    phaseIndex,
  });

  if (
    pathway.economicQualificationPosture === 'unsupported-incomplete' ||
    pathway.phases.length === 0
  ) {
    const phaseLabel = phase.label || `Phase ${phaseIndex + 1}`;
    return {
      ...pathway,
      id: `${pathway.id}_phase_${phaseIndex}`,
      name: `${phaseLabel} ${legLabel(tech, component)} (Hold)`,
    };
  }

  // Resize to the PHASE. `phase.capacityMW` is the datacenter LOAD; the
  // generation NAMEPLATE must be grossed up to SERVE it (same load→nameplate
  // source of truth as the preset path — resolveGenerationNameplateForTech).
  // The phase load is resolved through the power basis (PUE-aware) via
  // phaseFacilityLoadMW, then grossed up. We carry the LOAD on facilityLoadMW so
  // the demand charge keeps tracking the load, not the larger nameplate.
  const phaseLoadMw = phaseFacilityLoadMW(phase, intent);
  if (typeof phaseLoadMw === 'number' && phaseLoadMw > 0) {
    const nameplateMw = resolveGenerationNameplateForTech(phaseLoadMw, tech);
    const sizedMw = Number.isFinite(nameplateMw) && nameplateMw > 0 ? nameplateMw : phaseLoadMw;
    pathway = applyCapacityOverride(pathway, sizedMw);
    pathway = { ...pathway, facilityLoadMW: phaseLoadMw };
  }

  const codYear = phaseCodYear(phase, now);
  if (codYear != null) {
    pathway = applyCodYearOverride(pathway, codYear, now);
  }

  // Month-resolution COD label ("Jun 2029") when the user typed an exact date,
  // else the year ("2029"), else the pathway-derived COD year. Distinguishes
  // same-year milestones without fabricating a month where none was entered.
  const labelCod = phaseCodLabel(phase, now) ?? String(pathwayCodYearOf(pathway, now));
  const phaseLabel = phase.label || `Phase ${phaseIndex + 1}`;
  return {
    ...pathway,
    id: `${pathway.id}_phase_${phaseIndex}`,
    name: `${phaseLabel} ${legLabel(tech, component)} ${labelCod}`,
  };
}

/** COD year of an already-built pathway (used only for labelling). */
function pathwayCodYearOf(pathway: Pathway, now: Date): number {
  // pathwayDisplayNameWithCod appends the resolved COD year; reuse it then
  // pull the trailing 4-digit year back out so labelling stays consistent.
  const stamped = pathwayDisplayNameWithCod(pathway, now);
  const match = stamped.match(/(\d{4})$/);
  return match ? Number(match[1]) : now.getUTCFullYear();
}

/**
 * Build the blended (AND-mode) pathway for a phase: a single pathway whose
 * construction + operation phases carry a `sourceMix` of every GENERATION
 * component (storage/battery and null-bridge components are excluded from the
 * mix — they have no `sourceEconomicsFor` entry). The owning pathway is
 * anchored on the dominant (largest-MW) generation source's preset so its
 * lifetime / risk / revenue defaults are sensible, but capex + annual MWh are
 * derived from the mix by the cashflow engine. Returns null when the phase has
 * fewer than one bridgeable generation source.
 *
 * CARRY-FORWARD: each source's stored `componentParams[s].capacityMW` is the
 * CUMULATIVE capacity at this phase (the prior phase's infra carries forward),
 * so each leg's `fractionMw` must be the per-source INCREMENT added THIS phase
 * (`perSourceIncrementMW`), NOT the cumulative — otherwise every phase would
 * book the WHOLE stack's capex/energy. The `mw > 0` guard below filters out any
 * carried source whose increment is 0 (carried but not grown this phase), which
 * is also required because `blendPhaseEconomics` throws on `fractionMw <= 0`.
 * Needs the full sorted `phases` list to difference against the prior phase.
 */
function blendedPathwayForPhase(
  phase: ProjectPhase,
  phaseIndex: number,
  allPhases: ReadonlyArray<ProjectPhase>,
  intent: ProjectIntent,
  siteContext: SiteContext,
  now: Date,
): Pathway | null {
  const cfg = phase.powerSourceConfig;
  if (!cfg) return null;

  // Collect generation legs CARRYING their originating component, so a recip
  // leg stays distinguishable from a direct gas-sc source (both → 'gas-sc' tech,
  // distinct labels) and the blend name surfaces the TRUE legs (not "Blend").
  const legs: PhaseGenerationLeg[] = [];
  for (const component of cfg.components) {
    const res = resolvePhaseComponentTechnology(component, cfg);
    if (res.kind === 'storage') continue;
    if (
      res.kind === 'unresolved-configuration' ||
      res.kind === 'compare-all-suitable'
    ) {
      return buildUnsupportedIncompletePhasePathway(
        phase,
        phaseIndex,
        intent,
        'hold-fuel-cell-mix-ambiguous-or-unspecified',
        res.kind === 'compare-all-suitable'
          ? 'Stationary fuel-cell chemistry cannot be set to compare-all-suitable inside an AND-mode physical blend; select a specific chemistry.'
          : 'Stationary fuel-cell chemistry or primary fuel is unspecified — configuration remains unresolved and cannot default to SOFC.',
      );
    }
    const tech = res.primaryTechnologyId;
    if (!tech) continue;
    // Per-source INCREMENT (the additional capacity added THIS phase), not the
    // carried-forward cumulative — "calcs use only the additional capacity".
    const mw = perSourceIncrementMW(allPhases, component, phaseIndex);
    if (typeof mw === 'number' && mw > 0) {
      legs.push({ tech, component, fractionMw: mw });
    }
  }

  if (legs.length === 0) return null;

  // DEFENSIVE single-source fall-through: an AND phase that resolves to a single
  // distinct generation tech is NOT a hybrid blend — emit a single-tech pathway
  // so it gets a distinct, technology-specific name + classification (e.g. a
  // grid-only AND phase shows "Grid import", not "Blend"). Recip-aware naming is
  // preserved via the originating component.
  const distinctTechs = new Set(legs.map((l) => l.tech));
  if (distinctTechs.size === 1) {
    const only = legs[0];
    return singleTechPathwayForPhase(
      only.tech,
      phase,
      phaseIndex,
      intent,
      siteContext,
      now,
      only.component,
    );
  }

  const hasBatteryInMix = cfg.components.includes('battery');
  const sourceMix: PhaseSourceMixComponent[] = [];
  for (const l of legs) {
    if (isFuelCellTechnologyId(l.tech)) {
      const snap = buildFuelCellEconomicSnapshot({
        technologyId: l.tech,
        config: cfg.fuelCellConfig,
        gridConnection: cfg.gridConnection,
        hasBatteryInMix,
      });
      if (snap.economicQualificationPosture === 'unsupported-incomplete') {
        return buildUnsupportedIncompletePhasePathway(
          phase,
          phaseIndex,
          intent,
          snap.relianceHolds[0]?.id ?? 'hold-fuel-cell-incomplete',
          snap.relianceHolds[0]?.summary ?? 'Incomplete fuel-cell mix leg.',
        );
      }
      sourceMix.push({
        powerSource: l.tech,
        fractionMw: l.fractionMw,
        fuelCellSnapshot: snap,
      });
    } else {
      sourceMix.push({
        powerSource: l.tech,
        fractionMw: l.fractionMw,
      });
    }
  }

  // Anchor on the largest-MW source so lifetime / risk defaults are sensible.
  const dominant = legs.reduce((a, b) => (b.fractionMw > a.fractionMw ? b : a));
  const dominantFactory = PRESET_BY_TECHNOLOGY[dominant.tech];
  if (!dominantFactory) {
    throw new RangeError(
      `buildBlendedPathway: dominant leg '${dominant.tech}' has no preset factory. ` +
        'Mix legs must be point sources (a leg cannot itself be a blend).',
    );
  }
  const base = dominantFactory(intent, siteContext);

  // Replace the cost-bearing phases with sourceMix-carrying ones (dropping the
  // explicit capex / annualMwh so the blend drives them — mirrors the
  // phaseSourceMix engine contract).
  const phases = base.phases.map((p) => {
    if (p.kind === 'construction') {
      const { capexUsd: _drop, ...rest } = p;
      void _drop;
      return { ...rest, sourceMix };
    }
    if (p.kind === 'operation') {
      const { annualMwh: _dropMwh, ...rest } = p;
      void _dropMwh;
      return { ...rest, sourceMix };
    }
    return p;
  });

  const blendedCapacityMw = blendPhaseEconomics(sourceMix).capacityMw;

  // CAPEX INVARIANT (R-131-FIN-DECISION-C): `phase.capexUsd` — here derived from
  // the `sourceMix` at cashflow time — is the ONLY spend basis;
  // `capex.substationUsd` / `capex.transmissionUpgradeUsd` are DESCRIPTIVE
  // components that must sum INTO it. Inherited verbatim from the DOMINANT
  // preset they described a different pathway (a standalone grid connection
  // sized to the WHOLE load, not this blend's grid leg) — and the engine used to
  // re-add them on top of the blended capex, double-charging the connection.
  // Re-derive them from the grid leg using the SAME flat $/kW benchmarks
  // `blendPhaseEconomics` prices a `grid-tariff` component with (500 + 150
  // USD/kW), so they sum into the blended phase capex exactly; drop them when
  // the mix carries no grid leg.
  // NOTE (pre-existing, out of scope): `capex.baseUsd` is still the dominant
  // preset's own base, and the engine charges contingency % against it.
  const gridLegKw =
    legs
      .filter((l) => l.tech === 'grid-tariff')
      .reduce((sum, l) => sum + l.fractionMw, 0) * KW_PER_MW; // MW → kW
  const blendCapex: Pathway['capex'] =
    gridLegKw > 0
      ? {
          ...base.capex,
          // USD = kW × USD/kW.
          substationUsd: gridLegKw * GRID_TARIFF_BENCHMARKS.substationUsdPerKw,
          transmissionUpgradeUsd:
            gridLegKw * GRID_TARIFF_BENCHMARKS.transmissionUpgradeUsdPerKw,
        }
      : {
          ...base.capex,
          substationUsd: undefined,
          transmissionUpgradeUsd: undefined,
        };

  const phaseLoadMw = phaseFacilityLoadMW(phase, intent);
  let pathway: Pathway = {
    ...base,
    ...(typeof phaseLoadMw === 'number' && phaseLoadMw > 0 ? { facilityLoadMW: phaseLoadMw } : {}),
    capex: blendCapex,
    // Real classification: grid+on-site or multi-BTM mixes are 'hybrid'; the
    // single-distinct-tech case never reaches here (it fell through above).
    classification: classifyMix(legs),
    // `technology: 'mixed'` keeps the cashflow blend contract (the engine drives
    // capex/MWh from the sourceMix); the NAME below now exposes the true legs.
    technology: 'mixed',
    capacityMW: blendedCapacityMw,
    phases,
  };

  const codYear = phaseCodYear(phase, now);
  if (codYear != null) {
    pathway = applyCodYearOverride(pathway, codYear, now);
  }

  // Month-resolution COD label ("Jun 2029") when the user typed an exact date,
  // else the year, else the pathway-derived COD year.
  const labelCod = phaseCodLabel(phase, now) ?? String(pathwayCodYearOf(pathway, now));
  const phaseLabel = phase.label || `Phase ${phaseIndex + 1}`;
  // Compose the name from the actual legs (recip-aware) instead of "Blend", e.g.
  // "Phase 1 Grid import + Reciprocating Engine 2030".
  const legNames = legs.map((l) => legLabel(l.tech, l.component)).join(' + ');
  return {
    ...pathway,
    id: `mixed_phase_${phaseIndex}`,
    name: `${phaseLabel} ${legNames} ${labelCod}`,
  };
}

/**
 * Build intent-FAITHFUL pathways from a project's phased rollout.
 *
 * For EACH phase (in sequence) read its `powerSourceConfig`:
 *   - `mixMode === 'and'`  → ONE blended pathway carrying the per-source
 *     `sourceMix` (generation components only), at the phase capacity + COD.
 *   - `mixMode === 'or'` (or unset / multi-select) → ONE candidate pathway
 *     PER bridgeable generation component (alternatives to compare), each a
 *     single-tech pathway sized + anchored to the phase.
 *
 * Battery (and any null-bridge component) is skipped for single-tech
 * alternatives but kept as storage context inside an AND blend. Phases with
 * no `powerSourceConfig` fall back to the legacy `powerSource` key when it
 * bridges to a single tech; fully-undecided phases contribute nothing.
 *
 * Returns an empty array when the intent has no phased information to walk —
 * callers should fall back to `recommendedPathwaysForIntent`.
 */
export function pathwaysFromIntentPhases(
  intent: ProjectIntent | null | undefined,
  siteContext: SiteContext,
  now: Date = new Date(),
): Pathway[] {
  if (!intent) return [];
  const view = effectivePhases(intent);
  const pathways: Pathway[] = [];

  view.phases.forEach((phase, phaseIndex) => {
    const cfg = phase.powerSourceConfig;

    if (cfg && cfg.mixMode === 'and') {
      // Pass `view.phases` (sorted by sequence) so blendedPathwayForPhase can
      // difference each source's cumulative MW against the prior phase.
      const blended = blendedPathwayForPhase(phase, phaseIndex, view.phases, intent, siteContext, now);
      if (blended) pathways.push(blended);
      return;
    }

    // OR / unset / multi-select → one candidate per bridgeable component.
    const components = cfg?.components ?? [];
    if (components.length > 0) {
      const seen = new Set<TechnologyId>();
      for (const component of components) {
        const res = resolvePhaseComponentTechnology(component, cfg);
        if (res.kind === 'storage') continue;
        if (res.kind === 'unresolved-configuration') {
          pathways.push(
            buildUnsupportedIncompletePhasePathway(
              phase,
              phaseIndex,
              intent,
              'hold-fuel-cell-chemistry-unspecified',
              'Stationary fuel-cell chemistry or primary fuel is unspecified — configuration remains unresolved and cannot default to SOFC.',
            ),
          );
          continue;
        }
        for (const tech of res.technologyIds) {
          if (seen.has(tech)) continue;
          seen.add(tech);
          const candidatePhase: ProjectPhase =
            res.kind === 'compare-all-suitable' && isFuelCellTechnologyId(tech)
              ? {
                  ...phase,
                  powerSourceConfig: {
                    ...cfg!,
                    fuelCellConfig: {
                      ...cfg?.fuelCellConfig,
                      chemistry:
                        tech === 'fuel-cell-sofc'
                          ? 'sofc'
                          : tech === 'fuel-cell-pafc'
                            ? 'pafc'
                            : tech === 'fuel-cell-pem'
                              ? 'pem'
                              : 'mcfc',
                      primaryFuel:
                        tech === 'fuel-cell-pem'
                          ? 'hydrogen'
                          : cfg?.fuelCellConfig?.primaryFuel &&
                              cfg.fuelCellConfig.primaryFuel !== 'unspecified'
                            ? cfg.fuelCellConfig.primaryFuel
                            : 'natural-gas',
                    },
                  },
                }
              : phase;
          pathways.push(
            singleTechPathwayForPhase(
              tech,
              candidatePhase,
              phaseIndex,
              intent,
              siteContext,
              now,
              component,
            ),
          );
        }
      }
      return;
    }

    // No hybrid config — fall back to the legacy single `powerSource` key.
    const legacy = legacyPowerSourceToTechnologyId(phase.powerSource);
    if (legacy != null) {
      pathways.push(
        singleTechPathwayForPhase(legacy, phase, phaseIndex, intent, siteContext, now),
      );
    }
  });

  return pathways;
}

/** Bridge the coarse legacy `powerSource` key onto a finance preset id. */
function legacyPowerSourceToTechnologyId(
  source: ProjectPhase['powerSource'],
): TechnologyId | null {
  switch (source) {
    case 'grid':
      return 'grid-tariff';
    case 'gas-cc':
      return 'gas-cc';
    case 'gas-sc':
      return 'gas-sc';
    case 'smr':
      return 'nuclear-smr';
    case 'large-nuclear':
      return 'nuclear-large';
    case 'solar-bess':
      return 'solar-bess';
    case 'wind-bess':
      return 'wind-bess';
    case 'hybrid':
    case 'undecided':
    default:
      return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Feasibility-gated BTM technology set (Part B) — the candidate technologies a
// user may TEST in the comparison, gated to those whose firm capacity (ELCC) can
// SERVE the project's phase load. Reuses the SAME feasibility engine + sizing
// constants as `assessPhaseFeasibility` / `resolveGenerationNameplateForTech` —
// no new economics, no re-derivation.
// ─────────────────────────────────────────────────────────────────────────────

/** Candidate BTM (on-site) generation technologies a user can test. */
export const CANDIDATE_BTM_TECHNOLOGIES: readonly TechnologyId[] = [
  'gas-cc',
  'gas-sc',
  'fuel-cell-sofc',
  'fuel-cell-pafc',
  'fuel-cell-pem',
  'fuel-cell-mcfc',
  'nuclear-smr',
  'nuclear-large',
  'solar-bess',
  'wind-bess',
] as const;

export interface FeasibleTechnologyVerdict {
  technology: TechnologyId;
  /** Best (least-constrained) verdict across the project's phases. */
  verdict: 'feasible' | 'marginal' | 'infeasible';
  /** True when the technology can serve the load (feasible OR marginal). */
  selectable: boolean;
  /** One-line rationale from the binding phase's feasibility message. */
  message: string;
}

/**
 * Compute, per candidate BTM technology, whether a single source of that tech —
 * SIZED to serve the project's phase load via `resolveGenerationNameplateForTech`
 * (the same load→nameplate source of truth the presets use) — can FIRM the load
 * with the planning reserve. This is the "feasible combinations / replacements"
 * set the toggle gates on.
 *
 * The load denominator per phase is `phaseFacilityLoadMW` (PUE-aware), falling
 * back to the intent-declared firm-capacity basis (powerRequirementMW /
 * capacityCreditIntent.targetMW) when a phase carries no load — identical to the
 * `assessPhaseFeasibility` site-context fallback. A technology is `selectable`
 * when it is feasible/marginal for AT LEAST ONE phase (its best phase verdict).
 *
 * Pure + deterministic. `grid-tariff` is intentionally NOT a candidate here — it
 * is the always-available baseline (handled by the caller).
 */
export function feasibleTechnologiesForIntent(
  intent: ProjectIntent | null | undefined,
): FeasibleTechnologyVerdict[] {
  const out: FeasibleTechnologyVerdict[] = [];
  if (!intent) return out;

  const view = effectivePhases(intent);
  const siteCtx = firmCapacitySiteContextFromIntent(intent);
  const fallbackLoad =
    siteCtx?.fallbackLoadMW != null && siteCtx.fallbackLoadMW > 0 ? siteCtx.fallbackLoadMW : 0;

  // Resolve each phase's firm load once (PUE-aware), reused across all techs.
  const phaseLoads = view.phases.map((phase) => {
    const l = phaseFacilityLoadMW(phase, intent);
    if (typeof l === 'number' && Number.isFinite(l) && l > 0) return l;
    const declared = phase.capacityMW;
    if (typeof declared === 'number' && Number.isFinite(declared) && declared > 0) return declared;
    return fallbackLoad;
  });
  const loads = phaseLoads.filter((l) => l > 0);
  if (loads.length === 0) return out;

  const VERDICT_RANK = { feasible: 0, marginal: 1, infeasible: 2 } as const;

  for (const technology of CANDIDATE_BTM_TECHNOLOGIES) {
    const component = technologyIdToPhaseComponent(technology);
    if (component == null) continue;

    let best: FeasibleTechnologyVerdict | null = null;
    for (const load of loads) {
      // Size the source to serve THIS phase load, then assess a synthetic
      // single-source AND phase — the SAME firm-capacity arithmetic the report
      // gate uses. A source sized to its required nameplate firms the load by
      // construction, so a 'marginal'/'infeasible' here only arises if sizing
      // returned 0 (unbridgeable) — defensive.
      const nameplate = resolveGenerationNameplateForTech(load, technology);
      if (!Number.isFinite(nameplate) || nameplate <= 0) continue;
      const synthetic: ProjectPhase = {
        id: `__feas_${technology}`,
        label: 'feasibility probe',
        sequence: 1,
        capacityMW: load,
        powerSource: 'hybrid',
        status: 'planned',
        powerSourceConfig: {
          components: [component],
          mixMode: 'and',
          ...(isFuelCellTechnologyId(technology)
            ? {
                fuelCellConfig: {
                  chemistry:
                    technology === 'fuel-cell-sofc'
                      ? 'sofc'
                      : technology === 'fuel-cell-pafc'
                        ? 'pafc'
                        : technology === 'fuel-cell-pem'
                          ? 'pem'
                          : 'mcfc',
                },
              }
            : {}),
          componentParams: {
            [component]: {
              capacityMW: nameplate,
              capacityFactorPct: baseloadEnergyCfPct(component),
            },
          },
        },
      } as ProjectPhase;
      const f = assessPhaseFeasibility(synthetic);
      if (!f) continue;
      const cand: FeasibleTechnologyVerdict = {
        technology,
        verdict: f.verdict,
        selectable: f.verdict !== 'infeasible',
        message: f.message,
      };
      if (best == null || VERDICT_RANK[cand.verdict] < VERDICT_RANK[best.verdict]) {
        best = cand;
      }
    }
    if (best) out.push(best);
  }

  return out;
}
